import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { environment } from "../../../../../environments/environment";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import "./CompletionBanner.scss";

// Separate Tutorial Component
export const TutorialSteps = ({ showTutorial, setShowTutorial, tutorialStep, setTutorialStep }) => {
  const tutorialSteps = [
    {
      target: ".share-icon",
      content: "Share your community to attract more members!",
      position: "bottom",
      getPosition: () => {
        const shareButton = document.querySelector('.share-icon');
        if (shareButton) {
          const rect = shareButton.getBoundingClientRect();
          return {
            top: `${rect.bottom + 10}px`,
            left: `${rect.left - 150}px`,
            transform: 'translateX(50%)'
          };
        }
        return null;
      }
    },
    {
      target: "[data-tab='products']",
      content: "Add products and services to showcase what your community offers",
      position: "bottom",
      getPosition: () => {
        const productsTab = document.querySelector('button[data-tab="products"]');
        if (productsTab) {
          const rect = productsTab.getBoundingClientRect();
          return {
            top: `${rect.bottom + 10}px`,
            left: `${rect.left + (rect.width / 2) - 150}px`,
            transform: 'translateX(0)'
          };
        }
        return null;
      }
    }
  ];

  const handleNextStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
      setTutorialStep(0);
    }
  };

  // Update pointer position when tutorial step changes
  useEffect(() => {
    if (showTutorial) {
      const currentStep = tutorialSteps[tutorialStep];
      const position = currentStep.getPosition();
      if (position) {
        const pointer = document.querySelector('.tutorial-pointer');
        if (pointer) {
          Object.assign(pointer.style, {
            top: position.top,
            left: position.left,
            transform: position.transform
          });
        }
      }
    }
  }, [tutorialStep, showTutorial]);

  // Hide tutorial when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.tutorial-pointer') && !e.target.closest('.show-tutorial-btn')) {
        setShowTutorial(false);
        setTutorialStep(0);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return showTutorial ? (
    <div className="tutorial-overlay">
      <div 
        className="tutorial-pointer"
        style={{
          position: 'fixed',
          zIndex: 1001,
          width: '300px'
        }}
      >
        <div className="pointer-content">
          <p>{tutorialSteps[tutorialStep].content}</p>
          <button onClick={handleNextStep}>
            {tutorialStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

const CompletionBanner = ({ community, onShareClick, onCreatePost, adminPosts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const navigate = useNavigate();

  // Calculate completion based on whether admin has made a post
  const hasAdminPost = adminPosts.length > 0;
  const postCompletion = hasAdminPost ? 100 : 0;

  // Calculate customization completion
  const customizationFields = [
    'about',
    'whatsapp_group_link',
    'image',
    'banner_image'
  ];
  
  const completedFields = customizationFields.filter(field => 
    community && community[field] && community[field].length > 0
  );
  
  const customizationCompletion = Math.round((completedFields.length / customizationFields.length) * 100);

  const handleDismiss = async () => {
    try {
      const updatedData = { showTutorialBanner: false };
      const token = localStorage.getItem("accessToken");
      const response = await axios.patch(
        `${environment.baseUrl}/Communities/updateCommunity/${community._id}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDismissDialog(false);
    } catch (error) {
      console.error("Error updating tutorial banner status:", error);
    }
  };

  if (!community?.adminId || !community.showTutorialBanner) return null;

  return (
    <>
      <div className="admin-completion-banner">
        <div className="banner-controls">
          <button 
            className="show-tutorial-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowTutorial(true);
            }}
          >
            Show Tutorial
          </button>
          <button 
            className="dismiss-btn"
            onClick={() => setShowDismissDialog(true)}
          >
            Dismiss
          </button>
          <div 
            className="expand-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </div>
        </div>

        {isExpanded && (
          <div className="banner-content">
            {/* First Post Step */}
            <div className="step" onClick={onCreatePost}>
              <div className="step-header">
                <h6>Create Your First Community Post</h6>
                <span>{postCompletion}% Complete</span>
              </div>
              <div className="progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${postCompletion}%` }}
                />
              </div>
              <small>Share valuable content to engage your community members</small>
            </div>

            {/* Customization Step */}
            <div 
              className="step" 
              onClick={() => navigate(`/community/${community._id}`, { state: { activeTab: 'settings' } })}
            >
              <div className="step-header">
                <h6>Customize Your Community</h6>
                <span>{customizationCompletion}% Complete</span>
              </div>
              <div className="progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${customizationCompletion}%` }}
                />
              </div>
              <div className="customization-details">
                <small>Open your community to the world:</small>
                <ul>
                  {(
                    <li>Add a compelling description</li>
                  )}
                  {(
                    <li>Connect WhatsApp group</li>
                  )}
                  {(
                    <li>Upload community logo</li>
                  )}
                  {(
                    <li>Add cover image</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <TutorialSteps 
        showTutorial={showTutorial}
        setShowTutorial={setShowTutorial}
        tutorialStep={tutorialStep}
        setTutorialStep={setTutorialStep}
      />

      <Dialog open={showDismissDialog} onClose={() => setShowDismissDialog(false)}>
        <DialogTitle>Dismiss Tutorial Banner</DialogTitle>
        <DialogContent>
          <p>Once you dismiss this banner, you will only be able to access the tutorial from the settings page. Are you sure you want to proceed?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDismissDialog(false)}>Cancel</Button>
          <Button onClick={handleDismiss} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompletionBanner;